import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { setToken } from "@/lib/crmApi";
import { useAuth } from "@/context/CrmAuth";

/**
 * Organisation active et permissions du membre (plan V4 §40).
 *
 * Deux choses, et deux seulement :
 *
 *   - QUELLE organisation est active, et lesquelles sont accessibles ;
 *   - CE QUE le membre a le droit d'y faire, pour ne pas afficher des boutons
 *     qui échoueront en 403.
 *
 * Rien de ce qui est ici n'est une frontière de sécurité. Le backend décide
 * seul : il dérive l'organisation du jeton, jamais d'un champ envoyé par le
 * navigateur (§9). `permissions` sert à l'affichage — cacher une action
 * interdite est une politesse, pas une protection (§11, §40).
 *
 * Changer d'organisation passe par `POST /organizations/{id}/switch`, qui rend
 * un NOUVEAU jeton. C'est le seul chemin : réécrire l'identifiant côté client
 * ne donne rien.
 */
const OrgCtx = createContext(null);

export const useOrganization = () => useContext(OrgCtx) || {};

export function OrganizationProvider({ children }) {
  const { user } = useAuth();
  const [current, setCurrent] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!user) {
      setCurrent(null);
      setOrganizations([]);
      setPermissions([]);
      return;
    }
    setLoading(true);
    try {
      const [{ data: courante }, { data: liste }] = await Promise.all([
        api.get("/organizations/current"),
        api.get("/organizations"),
      ]);
      setCurrent(courante.organization);
      setPermissions(courante.permissions || []);
      setOrganizations(liste.items || []);
    } catch {
      // Un déploiement plus ancien n'expose pas encore ces routes : le CRM doit
      // rester utilisable, simplement sans sélecteur d'organisation.
      setCurrent(null);
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const switchTo = useCallback(async (organizationId) => {
    const { data } = await api.post(`/organizations/${organizationId}/switch`);
    setToken(data.token);
    // Rechargement complet : chaque écran a chargé les données de l'ancienne
    // organisation. Les rafraîchir un par un serait une source inépuisable de
    // fuites d'affichage — recharger est plus court et plus sûr.
    window.location.reload();
  }, []);

  const can = useCallback((permission) => permissions.includes(permission), [permissions]);

  const valeur = useMemo(
    () => ({ current, organizations, permissions, loading, reload: load, switchTo, can }),
    [current, organizations, permissions, loading, load, switchTo, can]
  );

  return <OrgCtx.Provider value={valeur}>{children}</OrgCtx.Provider>;
}
